export type CallStateStatus =
  | "initializing"
  | "connecting"
  | "connected"
  | "finished"

export type CallStateParticipantRole = "interpreter" | "client" | "other"

export type CallStateParticipant = {
  /*
   * Unique identifier of the participant
   */
  id: string

  /*
   * Display name
   */
  name: string

  /*
   * Role. `client` is the one who initiated the request.
   */
  role: CallStateParticipantRole

  /*
   * When they joined. Simplified ISO 8601 format.
   */
  joinedAt: string

  /*
   * When they left. Simplified ISO 8601 format.
   * Used to indicate what participants are no longer active.
   */
  leftAt: string | null

  /*
   * Does the participant have video enabled?
   */
  videoEnabled: boolean

  /*
   * Does the participant have audio enabled?
   */
  audioEnabled: boolean
}

export type CallStateEvent = {
  /*
   * The message action type
   */
  type: "amn:child/callState"

  payload: {
    /*
     * Call status.
     *
     * - initializing: call has not started.
     * - connecting: connecting to the party, including when disconnected and
     * retrying.
     * - connected: connected to the party.
     * - finished: the interpreter should drop from the call.
     */
    status: CallStateStatus

    /*
     * The call is in the "finished" state, but as a result of an error, which
     * may be displayed to the user and will be logged for troubleshooting. Set
     * to null when there is no error.
     */
    callFailureMessage: string | null

    /*
     * Is refreshing a call ready? `null` when not applicable.
     */
    refreshReady: boolean | null

    /*
     * Controls visibility of the "Add Interpreter" button on the interpreter workstation.
     * For when providers do not support multiple simultaneous interpreters.
     */
    multiInterpreterEnabled: boolean

    /*
     * The participants on the call. Can be empty until "connected".
     */
    participants: CallStateParticipant[]
  }
}

/*
 * The parent needs to be informed when the local media state has changed. For
 * example, if another participant mutes the interpreter or when the interpreter
 * has successfully setLocalMedia.
 *
 * When:
 * - The user's local media has changed for any reason.
 * - In response to a amn:parent/setLocalMedia or amn:parent/getLocalMediaState.
 */
export type LocalMediaStateEvent = {
  /*
   * The message action type
   */
  type: "amn:child/localMediaState"
  payload: {
    /*
     * The media type.
     */
    mediaType: "audio" | "video"

    /*
     * Whether the user's local media is enabled (unmuted) or disabled (muted).
     */
    enabled: boolean

    /*
     * User-friendly message when an error occured trying to set the local media.
     */
    failureMessage: string | null
  }
}

export type AmnOutgoingEvent = CallStateEvent | LocalMediaStateEvent

/**
 * We don't need factory functions for all of our event types, but this helper
 * utility is useful in more than one place.
 */
export const createLocalMediaStateEvent = (
  mediaType: "audio" | "video",
  enabled: boolean,
  failureMessage: string | null = null
): LocalMediaStateEvent => ({
  type: "amn:child/localMediaState",
  payload: {
    mediaType,
    enabled,
    failureMessage,
  },
})
